/**
 * ThisDeviceMediaTab — offline photo readiness for this computer.
 *
 * TCA-26100804, step 1 of the tab-wise restructure. Display only: there is no
 * action this tab can perform. Where the readiness model reports that the
 * operator must act — signing in again, for instance — the cause sentence says
 * so, and no button is offered, because signing in does not happen here. A
 * control that cannot do what it names is the defect this redesign removes.
 *
 * The status and its sentence come from the shared readiness presentation, so
 * the cause mapping has exactly one home.
 */
import React, { useMemo } from 'react';

import {
  formatMediaBytes,
  inventoryUnread,
  mediaReadinessPresentation,
  mediaRefreshPresentation,
  mediaDownloadPresentation,
} from '@/utils/mediaReadinessPresentation';
import { formatDateTimeWithZone } from '@/utils/dateUtils';
import DeviceStatusSlot, { slotTone } from './DeviceStatusSlot';
import SupportDetails from './SupportDetails';

export default function ThisDeviceMediaTab({
  status = null,
  loading = false,
  shopTimezone = null,
}) {
  // `desktop: true` is a fact here, not an assumption: SettingsView hides this
  // whole section unless the readiness bridge exists, so the desktop-required
  // state cannot be reached from this tab. It used to be a prop that no screen
  // set and only a test exercised. If the section gate ever changes, that gate
  // is the one place to change (TCA-26100804 closing audit).
  const view = useMemo(
    () => mediaReadinessPresentation(status, { loading, desktop: true }),
    [status, loading],
  );

  const unread = inventoryUnread(status);
  const counts = view.valid && !unread ? status.counts : null;
  const bytes = view.valid && !unread ? status.bytes : null;
  const refresh = view.valid ? mediaRefreshPresentation(status) : null;
  const download = view.valid ? mediaDownloadPresentation(status) : null;

  // A count is only meaningful once the inventory has been read. While it is
  // being checked, "All 0 photos ready" would state a zero nobody established.
  const lines = [];
  if (counts && !unread) {
    // Name what is being counted. Tightening the healthy state left "All 1,415
    // ready" with no subject, and the owner asked what the number referred to.
    // "Photos" matches the vocabulary the cause sentences already use
    // (TCA-26100804 closeout).
    lines.push(counts.pending > 0
      ? `${counts.available.toLocaleString()} of ${counts.eligible.toLocaleString()} photos ready · ${counts.pending.toLocaleString()} pending`
      : `All ${counts.eligible.toLocaleString()} photos ready`);
  }
  if (view.transfer) {
    lines.push(`Preparing ${view.transfer.label}: ${formatMediaBytes(view.transfer.transferredBytes)} of ${formatMediaBytes(view.transfer.expectedBytes)}`);
  }
  if (download && download.state !== 'unknown' && counts?.pending > 0
      && download.detail !== view.detail && ['catching_up', 'delayed'].includes(status.state)) lines.push(download.detail);
  if (refresh?.warning && ['preparing', 'catching_up'].includes(status.state)
      && status.cause !== 'offline_no_connection') {
    lines.push(refresh.warning);
  }

  // In the plain healthy state the cause sentence only repeats the count line
  // beneath it, leaving READY, Current, the sentence and the count all saying
  // the same thing. This is the rule the Product catalog tab already applies to
  // its reference-image sentence; a cause, outstanding work or a transfer all
  // keep it, because then it carries something the count does not
  // (TCA-26100804 closeout, owner-approved).
  const repeatsTheCount = view.valid
    && counts !== null
    && status?.state === 'current'
    && !status?.cause
    && !view.transfer;

  const stamp = (value) => (value ? formatDateTimeWithZone(value, shopTimezone) || null : null);

  return (
    <div className="space-y-3" data-testid="this-device-media-tab">
      <DeviceStatusSlot
        tone={slotTone(view.tone)}
        title={view.label}
        detail={repeatsTheCount ? null : view.detail}
        lines={lines}
        testId="media-readiness-slot"
      />
      <SupportDetails
        testId="media-support-details"
        rows={[
          ['Currently eligible', counts ? counts.eligible.toLocaleString() : null],
          ['Available', counts ? counts.available.toLocaleString() : null],
          ['Pending', counts ? counts.pending.toLocaleString() : null],
          ['Available bytes', bytes
            ? `${formatMediaBytes(bytes.available)} of ${formatMediaBytes(bytes.eligible)}` : null],
          ['Protected storage', view.valid
            ? `${formatMediaBytes(status.usageBytes)} of ${formatMediaBytes(status.budgetBytes)}` : null],
          ['Last completed refresh (this session)', view.valid ? stamp(refresh?.lastCompletedAt) || 'Not yet established' : null],
          ['Latest failed refresh (this session)', view.valid ? stamp(refresh?.lastFailedAt) || 'Not yet established' : null],
          ['Latest refresh failure', refresh?.failureDetail],
          ['Refresh in progress', view.valid ? (refresh ? (refresh.checking ? 'Yes' : 'No') : 'Not yet established') : null],
          ['Last accepted photo information', view.valid ? stamp(status.lastManifestSuccessAt) : null],
          ['Last storage maintenance', view.valid ? stamp(status.lastMaintenanceSuccessAt) : null],
          ['Download status', counts?.pending > 0 ? download?.detail : null],
          ['Reservation eligible for another check', counts?.pending > 0 ? stamp(download?.reservationExpiresAt) : null],
          ['Next scheduled check', counts?.pending > 0 ? stamp(download?.nextCheckAt) : null],
        ]}
      />
    </div>
  );
}
